import { Stack } from "@mui/material";
import { ChangeEvent, useMemo } from "react";
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  FormDataConsumer,
  Identifier,
  RaRecord,
  ReferenceArrayInput,
  ReferenceInput,
  SelectInput,
  required,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import CustomDateInput from "shared/components/inputs/CustomDateInput";
import { FinlandGroupTypeEnum, FranceGroupTypeEnum, SiteContryEnum } from "shared/enums/globalEnums";
import { finlandGroupTypeChoices } from "shared/enums/reactAdminChoices";
import { SiteResourcesEnum } from "shared/enums/resourceMappings";
import { validateEndDateAfterStartDate } from "shared/validators/dates";

const GroupSiteForm = ({ country }: { country: SiteContryEnum }) => {
  const { setValue } = useFormContext();
  const groupTypes = useMemo(() => {
    return Object.values(country === SiteContryEnum.France ? FranceGroupTypeEnum : FinlandGroupTypeEnum);
  }, [country]);

  const isFinland = country === SiteContryEnum.Finland;

  const handleGroupTypeChange = (event: ChangeEvent<HTMLInputElement> | RaRecord<Identifier>) => {
    setValue("groupType", event.target.value as FinlandGroupTypeEnum);
    setValue("group_id", undefined);
  };

  return (
    <>
      {isFinland ? (
        <>
          <SelectInput
            source="groupType"
            choices={finlandGroupTypeChoices}
            validate={[required()]}
            onChange={handleGroupTypeChange}
          />
          <FormDataConsumer>
            {({ formData }) => {
              const groupType = formData?.groupType as FinlandGroupTypeEnum | undefined;
              const disabled = !groupType;

              return (
                <GroupSiteFormContent
                  country={country}
                  groupFilter={{ type: groupType }}
                  siteFilter={{ country }}
                  disabled={disabled}
                />
              );
            }}
          </FormDataConsumer>
        </>
      ) : (
        <GroupSiteFormContent country={country} groupFilter={{ type__in: groupTypes }} siteFilter={{ country }} />
      )}
      <Stack direction="row" spacing={2} width={"100%"}>
        <CustomDateInput source="start_date" validate={[required(), validateEndDateAfterStartDate]} />
        <CustomDateInput source="end_date" validate={[validateEndDateAfterStartDate]} />
      </Stack>
    </>
  );
};

const GroupSiteFormContent = ({
  country,
  groupFilter,
  siteFilter,
  disabled = false,
}: {
  country: SiteContryEnum;
  groupFilter: Record<string, unknown>;
  siteFilter: Record<string, unknown>;
  disabled?: boolean;
}) => {
  return (
    <Stack direction="row" spacing={2} width={"100%"}>
      {country === SiteContryEnum.Finland ? (
        <ReferenceInput source="group_id" reference={SiteResourcesEnum.Groups} filter={groupFilter} perPage={100}>
          <AutocompleteInput label="Group" validate={[required()]} disabled={disabled} />
        </ReferenceInput>
      ) : (
        <ReferenceArrayInput source="group_ids" reference={SiteResourcesEnum.Groups} filter={groupFilter} perPage={100}>
          <AutocompleteArrayInput label="Groups" validate={[required()]} disabled={disabled} />
        </ReferenceArrayInput>
      )}
      <ReferenceArrayInput source="site_ids" reference={SiteResourcesEnum.Sites} filter={siteFilter} perPage={100}>
        <AutocompleteArrayInput label="Sites" validate={[required()]} disabled={disabled} />
      </ReferenceArrayInput>
    </Stack>
  );
};

export default GroupSiteForm;
